import React, { useCallback, useEffect, useRef, useState } from "react";

const API_URL = process.env.REACT_APP_API_URL || "";

function dollars(cents) {
  if (cents === null || cents === undefined) return "—";
  return (cents / 100).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

// "1,234.56" or "$1234.5" to whole cents, or null. Never floats the sum: the cents are
// parsed from the digits.
export function parseCents(text) {
  const clean = String(text || "").replace(/[$,\s]/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(clean)) return null;
  const [whole, frac = ""] = clean.split(".");
  const cents = Number(whole) * 100 + Number((frac + "00").slice(0, 2));
  return cents > 0 ? cents : null;
}

async function send(url, method, body) {
  const response = await fetch(url, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error((payload && payload.error) || "The request failed.");
  }
  return payload;
}

// Deposits vs bank. The biller types the bank statement's lines in; Atlas proposes the
// posted ERAs within a cent and three days; a person clicks Confirm. Matching posts nothing
// and moves no money — it records that two records are the same deposit.
export default function BankReconciliation({
  practiceId,
  dateFrom,
  dateTo,
  readOnly = false,
}) {
  const [view, setView] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    deposit_date: dateTo || "",
    amount: "",
    payer_hint: "",
  });
  const ticket = useRef(0);
  const base = `${API_URL}/api/practices/${practiceId}/reports/bank-deposits`;

  const load = useCallback(async () => {
    if (!practiceId) return;
    const mine = ++ticket.current;
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const response = await fetch(`${base}?${params}`, {
        credentials: "include",
      });
      const body = await response.json().catch(() => null);
      if (mine !== ticket.current) return;
      if (!response.ok || !body) {
        setError(
          (body && body.error) || "Could not load the bank reconciliation.",
        );
        return;
      }
      setView(body);
      setError(null);
    } catch (_) {
      if (mine === ticket.current)
        setError("Could not load the bank reconciliation.");
    }
  }, [base, practiceId, dateFrom, dateTo]);

  useEffect(() => {
    setView(null);
    load();
  }, [load]);

  const act = async (fn) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const add = (e) => {
    e.preventDefault();
    const cents = parseCents(form.amount);
    if (!form.deposit_date || cents === null) {
      setError("Enter the deposit date and an amount like 1234.56.");
      return;
    }
    act(async () => {
      await send(base, "POST", {
        deposit_date: form.deposit_date,
        amount_cents: cents,
        payer_hint: form.payer_hint || null,
      });
      setForm((f) => ({ ...f, amount: "", payer_hint: "" }));
    });
  };

  return (
    <div className="reports-card">
      <h3>Bank reconciliation</h3>
      <p className="reports-note">
        Enter the payer deposits from the bank statement. Atlas proposes posted
        ERAs within
        {view
          ? ` ${dollars(view.tolerance.amount_cents)} and ${view.tolerance.days} days`
          : " a cent and three days"}
        ; nothing is matched until you confirm.
      </p>

      {error && (
        <div className="reports-message error" role="alert">
          <span>{error}</span>
          <button
            className="message-close"
            aria-label="Dismiss"
            onClick={() => setError(null)}
          >
            &times;
          </button>
        </div>
      )}

      {!readOnly && (
        <form className="reports-controls" onSubmit={add}>
          <label>
            Bank date{" "}
            <input
              type="date"
              value={form.deposit_date}
              onChange={(e) =>
                setForm({ ...form, deposit_date: e.target.value })
              }
            />
          </label>
          <label>
            Amount{" "}
            <input
              type="text"
              inputMode="decimal"
              placeholder="1234.56"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />
          </label>
          <label>
            As on the statement{" "}
            <input
              type="text"
              maxLength={120}
              placeholder="AETNA HCCLAIMPMT"
              value={form.payer_hint}
              onChange={(e) => setForm({ ...form, payer_hint: e.target.value })}
            />
          </label>
          <button
            type="submit"
            className="btn btn-secondary btn-sm"
            disabled={busy}
          >
            Add bank line
          </button>
        </form>
      )}

      {view && (
        <table className="reports-table">
          <thead>
            <tr>
              <th>Bank date</th>
              <th className="num">Amount</th>
              <th>Statement text</th>
              <th>Status</th>
              <th>Matched ERA / proposals</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {view.bank_lines.length === 0 && (
              <tr>
                <td colSpan={6} className="reports-empty">
                  No bank lines entered for this range.
                </td>
              </tr>
            )}
            {view.bank_lines.map((line) => (
              <tr key={line.id}>
                <td>{line.deposit_date}</td>
                <td className="num">{dollars(line.amount_cents)}</td>
                <td>{line.payer_hint || "—"}</td>
                <td>
                  <span
                    className={`reports-badge reports-badge-${line.status}`}
                  >
                    {line.status}
                  </span>
                  {line.stale_match && (
                    <div className="reports-empty">ERA no longer posted — unmatch it</div>
                  )}
                </td>
                <td>
                  {line.matched.map((m) => (
                    <div key={m.remittance_id}>
                      {m.payer_name} {m.check_number || ""}{" "}
                      {dollars(m.amount_cents)} ({m.deposit_date})
                    </div>
                  ))}
                  {line.status === "unmatched" &&
                    line.proposals.length === 0 && (
                      <span className="reports-empty">
                        No ERA within tolerance
                      </span>
                    )}
                  {line.proposals.map((p) => (
                    <div key={p.remittance_id} className="reports-proposal">
                      {p.payer_name} {p.check_number || ""}{" "}
                      {dollars(p.amount_cents)} ({p.deposit_date},{" "}
                      {p.days_apart === 0
                        ? "same day"
                        : `${p.days_apart}d apart`}
                      )
                      {!readOnly && (
                        <button
                          type="button"
                          className="btn-link"
                          disabled={busy}
                          onClick={() =>
                            act(() =>
                              send(`${base}/${line.id}/match`, "POST", {
                                remittance_ids: [p.remittance_id],
                              }),
                            )
                          }
                        >
                          Confirm
                        </button>
                      )}
                    </div>
                  ))}
                </td>
                <td>
                  {!readOnly && line.status !== "unmatched" && (
                    <button
                      type="button"
                      className="btn-link"
                      disabled={busy}
                      onClick={() =>
                        act(() => send(`${base}/${line.id}/unmatch`, "POST"))
                      }
                    >
                      Unmatch
                    </button>
                  )}
                  {!readOnly && line.status === "unmatched" && (
                    <button
                      type="button"
                      className="btn-link"
                      disabled={busy}
                      onClick={() => {
                        // A typed-in line, not money; still a delete, so it asks.
                        if (!window.confirm(`Delete the ${line.deposit_date} bank line for ${dollars(line.amount_cents)}?`)) return;
                        act(() => send(`${base}/${line.id}`, "DELETE"));
                      }}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {view && (
        <div className="reports-grid reports-grid-even">
          <div>
            <h3>Bank lines not matched ({view.unmatched_bank.length})</h3>
            {view.unmatched_bank.map((l) => (
              <div key={l.id}>
                {l.deposit_date} {dollars(l.amount_cents)} {l.payer_hint || ""}{" "}
                <em>{l.status}</em>
              </div>
            ))}
          </div>
          <div>
            <h3>
              Posted ERAs with no bank line ({view.unmatched_remittances.length}
              )
            </h3>
            {view.unmatched_remittances.map((r) => (
              <div key={r.remittance_id}>
                {r.deposit_date} {r.payer_name} {r.check_number || ""}{" "}
                {dollars(r.amount_cents)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
